/**
 * Pure view-model layer for the three-pane API Reference page (SP-8216).
 *
 * Parses a fetched OpenAPI 3.x spec into flat, render-ready endpoint and schema
 * view-models, plus the small humanize / category / example-synthesis helpers
 * the page needs. Everything here is a pure function — no React, no I/O — so it
 * is exercised directly by apiReferenceModel.test.ts.
 */

import {
    HttpMethod,
    OpenApiOperation,
    OpenApiParameter,
    OpenApiSchema,
    OpenApiSpec,
} from './types';
import { collectParameters, describeSchema, refName } from './openApiUtils';

const HTTP_METHODS: HttpMethod[] = [
    'get',
    'put',
    'post',
    'delete',
    'patch',
    'options',
    'head',
    'trace',
];

export type ApiCategory = 'Dimension' | 'Data' | 'Collections';

/** A parameter flattened for the reference view + try-it console. */
export interface RefParam {
    name: string;
    in: string;
    required: boolean;
    description?: string;
    type: string;
    /** `loc` mirrors `in` for the ported buildSnippet contract. */
    loc: string;
    /** A synthesized placeholder value derived from the param's schema. */
    placeholder: string;
}

/** A single endpoint, flattened from `spec.paths` × method. */
export interface RefEndpoint {
    operationId: string;
    method: HttpMethod;
    path: string;
    summary?: string;
    description?: string;
    params: RefParam[];
    /** A one-object JSON example for the request body, when present. */
    requestBodyExample?: unknown;
    /** Schema name referenced by the 200 response, when resolvable. */
    returns?: string;
    /** Raw first tag (un-humanized) — also the nav group key. */
    group: string;
    /** Humanized group label for display. */
    groupLabel: string;
    category: ApiCategory;
}

/** A single field row inside a schema view. */
export interface RefField {
    name: string;
    type: string;
    description?: string;
    required: boolean;
    /** Target schema name when this field is a $ref (or array of $ref). */
    ref?: string;
}

export type SchemaGroup = 'Enums' | 'Query inputs' | 'Responses' | 'Core';

/** A single schema, flattened from `spec.components.schemas`. */
export interface RefSchema {
    name: string;
    description?: string;
    fields: RefField[];
    example: unknown;
    group: SchemaGroup;
}

/** The fully-parsed reference model the page renders from. */
export interface ApiReferenceModel {
    endpoints: RefEndpoint[];
    schemas: RefSchema[];
}

/* ------------------------------------------------------------------ */
/* Humanize / category                                                */
/* ------------------------------------------------------------------ */

const TITLE_CASE = (word: string): string =>
    word.length === 0 ? word : word[0].toUpperCase() + word.slice(1);

/** Naive-but-good-enough pluralizer for humanized group labels. */
const pluralize = (word: string): string => {
    if (!word) return word;
    if (/(s|x|z|ch|sh)$/i.test(word)) return `${word}es`;
    if (/[^aeiou]y$/i.test(word)) return `${word.slice(0, -1)}ies`;
    if (/s$/i.test(word)) return word;
    return `${word}s`;
};

/**
 * `media-value-report-query-controller` → "Media Value Reports".
 * Strips a trailing `-controller`, title-cases the tokens, and pluralizes the
 * final word so the nav reads like a noun group.
 */
export const humanizeGroup = (tag: string): string => {
    if (!tag) return 'Other';
    const cleaned = tag
        .replace(/-?controller$/i, '')
        .replace(/-?query$/i, '')
        .trim();
    const tokens = cleaned
        .split(/[-_\s]+/)
        .filter(Boolean)
        .map(TITLE_CASE);
    if (tokens.length === 0) return TITLE_CASE(tag);
    const last = tokens.length - 1;
    tokens[last] = pluralize(tokens[last]);
    return tokens.join(' ');
};

/**
 * Map a raw tag to one of the three nav categories. Matching is substring-based
 * against a small keyword lookup so controller-suffixed tags still resolve.
 * Unknown tags default to 'Data'.
 */
export const categoryForTag = (tag: string): ApiCategory => {
    const t = (tag || '').toLowerCase();
    const has = (...keys: string[]) => keys.some(k => t.includes(k));

    // Dimension: sponsors, rightsholders, platforms, streams
    if (has('sponsor') && !has('placement')) {
        return 'Dimension';
    }
    if (has('rightsholder', 'platform', 'stream')) {
        return 'Dimension';
    }
    // Collections: client RSPs, sponsor placements
    if (has('placement', 'rsp', 'collection')) {
        return 'Collections';
    }
    // Data: media-value-reports, llm-exposures, social-posts
    if (has('media-value', 'mediavalue', 'llm', 'exposure', 'social', 'post')) {
        return 'Data';
    }
    return 'Data';
};

/* ------------------------------------------------------------------ */
/* Example synthesis                                                   */
/* ------------------------------------------------------------------ */

/** A placeholder scalar for a given schema, used in snippets + try-it. */
export const placeholderForSchema = (
    schema: OpenApiSchema | undefined | null,
): string => {
    if (!schema) return '…';
    if (schema.type === 'integer' || schema.type === 'number') return '123';
    if (schema.type === 'boolean') return 'true';
    if (schema.type === 'string') {
        if (schema.format === 'date-time' || schema.format === 'date') {
            return '2025-01-01';
        }
        if (schema.enum && schema.enum.length > 0) {
            return String(schema.enum[0]);
        }
        return '…';
    }
    return '…';
};

/** Resolve a `$ref` (direct or array-item) to its target schema name. */
const refTarget = (schema: OpenApiSchema | undefined): string | undefined => {
    if (!schema) return undefined;
    if (schema.$ref) return refName(schema.$ref);
    if (schema.type === 'array' && schema.items?.$ref) {
        return refName(schema.items.$ref);
    }
    return undefined;
};

/* ------------------------------------------------------------------ */
/* Composition resolution                                              */
/* ------------------------------------------------------------------ */

type SchemaMap = Record<string, OpenApiSchema> | undefined;

/** Hard ceiling on resolver recursion — springdoc specs can self-reference. */
const MAX_RESOLVE_DEPTH = 8;

/**
 * Flatten an OpenAPI 3.0 schema's composition into an effective schema-like
 * object. springdoc-openapi 1.8 emits subclass DTOs as
 *   { allOf: [ { $ref: Base }, { type:'object', properties, required } ] }
 * which carry no top-level `properties`; the live view read only `properties`
 * and therefore showed "No fields." This resolver:
 *   - follows a top-level `$ref` into `components`,
 *   - merges `allOf` members' `properties` (later members win on key) and
 *     unions their `required` arrays,
 *   - carries through scalar shape fields from the base when present,
 *   - guards against cycles via a `seen` set of ref names + a max depth.
 * `oneOf`/`anyOf` are not true intersections — we lazily take the first member.
 */
export const resolveSchema = (
    schema: OpenApiSchema | undefined | null,
    components: SchemaMap,
    seen: Set<string> = new Set(),
    depth = 0,
): OpenApiSchema => {
    if (!schema) return {};
    if (depth >= MAX_RESOLVE_DEPTH) return schema;

    // Follow a top-level $ref into the components map.
    if (schema.$ref) {
        const name = refName(schema.$ref);
        if (seen.has(name)) return schema;
        const target = components?.[name];
        if (!target) return schema;
        const nextSeen = new Set(seen);
        nextSeen.add(name);
        return resolveSchema(target, components, nextSeen, depth + 1);
    }

    // oneOf/anyOf: lazily resolve the first member (documented limitation).
    const variant = schema.oneOf?.[0] ?? schema.anyOf?.[0];
    if (variant && !schema.allOf) {
        return resolveSchema(variant, components, seen, depth + 1);
    }

    if (!schema.allOf) return schema;

    // Merge every allOf member into one effective schema.
    const mergedProperties: Record<string, OpenApiSchema> = {
        ...(schema.properties ?? {}),
    };
    const mergedRequired = new Set<string>(schema.required ?? []);
    const effective: OpenApiSchema = { ...schema };
    delete effective.allOf;

    schema.allOf.forEach(member => {
        const resolved = resolveSchema(member, components, seen, depth + 1);
        if (resolved.properties) {
            // Later members win on duplicate keys.
            Object.assign(mergedProperties, resolved.properties);
        }
        (resolved.required ?? []).forEach(r => mergedRequired.add(r));
        // Carry through shape fields from a member when not already set.
        (['type', 'items', 'enum', 'format', 'description', 'example'] as const).forEach(
            key => {
                if (effective[key] === undefined && resolved[key] !== undefined) {
                    // key is a literal union; the index signature makes the
                    // value `unknown`, so widen the target to assign.
                    (effective as Record<string, unknown>)[key] = resolved[key];
                }
            },
        );
    });

    effective.properties = mergedProperties;
    effective.required = Array.from(mergedRequired);
    if (effective.type === undefined && Object.keys(mergedProperties).length > 0) {
        effective.type = 'object';
    }
    return effective;
};

/**
 * Synthesize a representative JS value for a schema property from its declared
 * type (used when the spec gives us no `example`). When a `components` map is
 * supplied, `$ref` and `allOf` composition are resolved one useful level deep
 * into a nested example object; the same `seen`/`depth` guard prevents infinite
 * recursion on self-referential springdoc specs (falling back to the `<Name>`
 * string or `{}` once the budget is exhausted).
 */
const synthValue = (
    schema: OpenApiSchema | undefined,
    components?: SchemaMap,
    seen: Set<string> = new Set(),
    depth = 0,
): unknown => {
    if (!schema) return null;
    if (schema.example !== undefined) return schema.example;

    // A direct $ref: resolve one level into a nested object when we can.
    if (schema.$ref) {
        const name = refName(schema.$ref);
        if (!components || seen.has(name) || depth >= MAX_RESOLVE_DEPTH) {
            return `<${name}>`;
        }
        const resolved = resolveSchema(schema, components, seen, depth);
        if (resolved.$ref) return `<${name}>`;
        const nextSeen = new Set(seen);
        nextSeen.add(name);
        return synthValue(resolved, components, nextSeen, depth + 1);
    }

    if (schema.enum && schema.enum.length > 0) return schema.enum[0];

    // Composition without a $ref wrapper — flatten before synthesizing.
    if (schema.allOf || schema.oneOf || schema.anyOf) {
        const resolved = resolveSchema(schema, components, seen, depth);
        if (resolved.allOf || resolved.oneOf || resolved.anyOf) return {};
        return synthValue(resolved, components, seen, depth + 1);
    }

    switch (schema.type) {
        case 'integer':
        case 'number':
            return 0;
        case 'boolean':
            return true;
        case 'array':
            return [synthValue(schema.items, components, seen, depth + 1)];
        case 'string':
            if (schema.format === 'date-time') return '2025-01-01T00:00:00Z';
            if (schema.format === 'date') return '2025-01-01';
            return 'string';
        case 'object':
        default:
            if (schema.properties) {
                const out: Record<string, unknown> = {};
                Object.entries(schema.properties).forEach(([k, v]) => {
                    out[k] = synthValue(v, components, seen, depth + 1);
                });
                return out;
            }
            return {};
    }
};

/**
 * A whole-schema example: prefer the declared one, else synthesize. `components`
 * is optional so callers (and tests) can pass just a schema; when supplied,
 * composed/inherited schemas synthesize their full field set.
 */
export const synthesizeExample = (
    schema: OpenApiSchema,
    components?: SchemaMap,
): unknown => {
    if (schema.example !== undefined) return schema.example;
    const resolved = resolveSchema(schema, components);
    if (resolved.example !== undefined) return resolved.example;
    return synthValue(resolved, components);
};

/* ------------------------------------------------------------------ */
/* Schema grouping                                                     */
/* ------------------------------------------------------------------ */

export const schemaGroupFor = (
    name: string,
    schema: OpenApiSchema,
): SchemaGroup => {
    if (schema.enum && schema.enum.length > 0) return 'Enums';
    if (name.startsWith('ListWithTotal')) return 'Responses';
    if (name.endsWith('Query')) return 'Query inputs';
    return 'Core';
};

/* ------------------------------------------------------------------ */
/* Parsers                                                             */
/* ------------------------------------------------------------------ */

const slugId = (method: string, path: string): string =>
    `${method}_${path}`
        .replace(/[{}]/g, '')
        .replace(/[^a-zA-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');

const mapParam = (param: OpenApiParameter): RefParam => ({
    name: param.name ?? '',
    in: param.in ?? 'query',
    required: Boolean(param.required),
    description: param.description,
    type: describeSchema(param.schema),
    loc: param.in ?? 'query',
    placeholder: placeholderForSchema(param.schema),
});

/** Pull a one-object JSON example out of an operation's JSON request body. */
const requestBodyExample = (
    operation: OpenApiOperation,
    components?: SchemaMap,
): unknown | undefined => {
    const media = operation.requestBody?.content?.['application/json'];
    if (!media) return undefined;
    if (media.schema?.example !== undefined) return media.schema.example;
    if (media.schema) return synthValue(media.schema, components);
    return undefined;
};

/** Schema name referenced by the operation's 200 response, if resolvable. */
const responseReturns = (operation: OpenApiOperation): string | undefined => {
    const ok = operation.responses?.['200'] ?? operation.responses?.['201'];
    const schema = ok?.content?.['application/json']?.schema;
    return refTarget(schema);
};

export const parseEndpoints = (
    spec: OpenApiSpec | null | undefined,
): RefEndpoint[] => {
    if (!spec?.paths) return [];
    const components = spec.components?.schemas;
    const out: RefEndpoint[] = [];

    Object.entries(spec.paths).forEach(([path, pathItem]) => {
        if (!pathItem) return;
        HTTP_METHODS.forEach(method => {
            const operation = pathItem[method] as OpenApiOperation | undefined;
            if (!operation) return;

            const group =
                operation.tags && operation.tags.length > 0
                    ? operation.tags[0]
                    : 'Other';

            const params = collectParameters(
                pathItem.parameters,
                operation.parameters,
            ).map(mapParam);

            out.push({
                operationId: operation.operationId || slugId(method, path),
                method,
                path,
                summary: operation.summary,
                description: operation.description,
                params,
                requestBodyExample: requestBodyExample(operation, components),
                returns: responseReturns(operation),
                group,
                groupLabel: humanizeGroup(group),
                category: categoryForTag(group),
            });
        });
    });

    return out;
};

export const parseSchemas = (
    spec: OpenApiSpec | null | undefined,
): RefSchema[] => {
    const schemas = spec?.components?.schemas;
    if (!schemas) return [];

    return Object.entries(schemas).map(([name, schema]) => {
        // Flatten allOf/$ref composition so inherited fields surface (AC2).
        const effective = resolveSchema(schema, schemas);
        const properties = effective.properties ?? {};
        const required = new Set<string>(
            (effective.required as string[] | undefined) ?? [],
        );
        const fields: RefField[] = Object.entries(properties).map(
            ([fieldName, prop]) => ({
                name: fieldName,
                type: describeSchema(prop),
                description: prop?.description,
                required: required.has(fieldName),
                ref: refTarget(prop),
            }),
        );

        return {
            name,
            description: schema.description ?? effective.description,
            fields,
            example: synthesizeExample(schema, schemas),
            group: schemaGroupFor(name, schema),
        };
    });
};

/** Top-level parse: spec → full reference model. */
export const parseApiReference = (
    spec: OpenApiSpec | null | undefined,
): ApiReferenceModel => ({
    endpoints: parseEndpoints(spec),
    schemas: parseSchemas(spec),
});
